(() => {
  const CHECKOUT_CONTROL_SELECTOR =
    'button[type="submit"][name="checkout"], input[type="submit"][name="checkout"]';
  const CART_DRAWER_SELECTOR = 'cart-drawer-component';
  const CART_DRAWER_DIALOG_SELECTOR = 'dialog.cart-drawer__dialog[open]';
  const CART_DRAWER_TRIGGER_SELECTOR = '[data-testid="cart-drawer-trigger"]';
  const RAZORPAY_FRAME_SELECTOR =
    'iframe.razorpay-checkout-frame, iframe.razorpay-sopc-frame, .razorpay-container iframe, .razorpay-sopc-container iframe';
  const RAZORPAY_ABORT_ALERT = 'Something went wrong, please try again after some time.';
  const RAZORPAY_ROOT_DOMAINS = ['razorpay.com', 'razorpay.in'];
  const CHECKOUT_LOADER_DELAY_MS = 350;
  const MAGIC_RENDER_TIMEOUT_MS = 15000;
  const DRAWER_CLOSE_TIMEOUT_MS = 750;
  const MAGIC_ATTEMPT_STATE = Object.freeze({
    idle: 'idle',
    awaitingRender: 'awaiting-render',
    rendered: 'rendered',
    recovery: 'recovery',
    navigating: 'navigating',
  });

  let checkoutReplayInProgress = false;
  let drawerCheckoutInProgress = false;
  let nativeCheckoutInProgress = false;
  let magicAttemptState = MAGIC_ATTEMPT_STATE.idle;
  let magicRenderTimeoutId;
  let checkoutLoaderDelayId;
  let checkoutLoader;
  let checkoutFocusReturnTarget;
  let fallbackDialog;
  let fallbackReason = 'razorpay-render-timeout';
  let restoreAlertGuard;

  const focusWithoutScrolling = (target) => {
    if (!(target instanceof HTMLElement) || !target.isConnected) return;

    try {
      target.focus({ preventScroll: true });
    } catch (_error) {
      target.focus();
    }
  };

  const removeFallbackDialog = () => {
    if (!fallbackDialog) return;

    if (fallbackDialog.open) fallbackDialog.close();
    fallbackDialog.remove();
    fallbackDialog = undefined;
  };

  const clearMagicRenderTimeout = () => {
    if (magicRenderTimeoutId === undefined) return;

    window.clearTimeout(magicRenderTimeoutId);
    magicRenderTimeoutId = undefined;
  };

  const clearCheckoutLoaderDelay = () => {
    if (checkoutLoaderDelayId === undefined) return;

    window.clearTimeout(checkoutLoaderDelayId);
    checkoutLoaderDelayId = undefined;
  };

  const removeCheckoutLoader = () => {
    clearCheckoutLoaderDelay();
    checkoutLoader?.remove();
    checkoutLoader = undefined;
  };

  const showCheckoutLoader = () => {
    if (checkoutLoader?.isConnected || magicAttemptState !== MAGIC_ATTEMPT_STATE.awaitingRender) return;

    checkoutLoader = document.createElement('div');
    checkoutLoader.className = 'hoy-checkout-loader';
    checkoutLoader.dataset.hoyCheckoutLoader = '';
    checkoutLoader.setAttribute('role', 'status');
    checkoutLoader.setAttribute('aria-live', 'polite');
    checkoutLoader.setAttribute('aria-atomic', 'true');
    checkoutLoader.tabIndex = -1;
    checkoutLoader.innerHTML = `
      <style>
        .hoy-checkout-loader {
          position: fixed;
          inset: 0;
          z-index: 2147483646;
          display: grid;
          place-items: center;
          padding: 24px;
          background: rgb(10 15 14 / 48%);
          -webkit-backdrop-filter: blur(2px);
          backdrop-filter: blur(2px);
          outline: none;
        }
        .hoy-checkout-loader__card {
          display: flex;
          align-items: center;
          gap: 14px;
          width: min(360px, calc(100vw - 40px));
          padding: 18px 20px;
          border-radius: 16px;
          color: #0a0f0e;
          background: #fff;
          box-shadow: 0 18px 60px rgb(0 0 0 / 28%);
        }
        .hoy-checkout-loader__spinner {
          flex: 0 0 auto;
          width: 26px;
          height: 26px;
          border: 3px solid #dce9e4;
          border-top-color: #283e36;
          border-radius: 50%;
          animation: hoy-checkout-spin 0.8s linear infinite;
        }
        .hoy-checkout-loader__copy { min-width: 0; }
        .hoy-checkout-loader__title {
          margin: 0;
          font-size: 16px;
          font-weight: 700;
          line-height: 1.3;
        }
        .hoy-checkout-loader__text {
          margin: 3px 0 0;
          color: #52605b;
          font-size: 13px;
          line-height: 1.4;
        }
        @keyframes hoy-checkout-spin { to { transform: rotate(360deg); } }
        @media (prefers-reduced-motion: reduce) {
          .hoy-checkout-loader__spinner { animation: none; }
        }
      </style>
      <div class="hoy-checkout-loader__card">
        <span class="hoy-checkout-loader__spinner" aria-hidden="true"></span>
        <div class="hoy-checkout-loader__copy">
          <p class="hoy-checkout-loader__title">Opening secure checkout&hellip;</p>
          <p class="hoy-checkout-loader__text">This usually takes only a few seconds.</p>
        </div>
      </div>
    `;

    document.body.appendChild(checkoutLoader);
    focusWithoutScrolling(checkoutLoader);
  };

  const scheduleCheckoutLoader = () => {
    removeCheckoutLoader();
    checkoutLoaderDelayId = window.setTimeout(() => {
      checkoutLoaderDelayId = undefined;
      showCheckoutLoader();
    }, CHECKOUT_LOADER_DELAY_MS);
  };

  const suppressRazorpayAbortAlert = () => {
    if (typeof window.alert !== 'function') return;

    restoreAlertGuard?.();
    const originalAlert = window.alert;
    const guardedAlert = (message) => {
      if (String(message).trim() === RAZORPAY_ABORT_ALERT) return;
      originalAlert.call(window, message);
    };
    const restore = () => {
      if (window.alert === guardedAlert) window.alert = originalAlert;
      if (restoreAlertGuard === restore) restoreAlertGuard = undefined;
    };

    window.alert = guardedAlert;
    restoreAlertGuard = restore;
    window.setTimeout(restore, 5000);
  };

  const continueToNativeCheckout = (reason = fallbackReason) => {
    if (nativeCheckoutInProgress) return;

    nativeCheckoutInProgress = true;
    magicAttemptState = MAGIC_ATTEMPT_STATE.navigating;
    clearMagicRenderTimeout();
    removeCheckoutLoader();
    fallbackDialog?.querySelectorAll('button').forEach((button) => {
      button.disabled = true;
    });
    fallbackDialog?.querySelector('[data-hoy-native-checkout]')?.setAttribute('aria-busy', 'true');

    try {
      window.sessionStorage.setItem('hoyCheckoutFallback', reason);
    } catch (_error) {
      // Storage can be unavailable in private or restricted browser modes.
    }

    document.dispatchEvent(new CustomEvent('hoy:checkout-fallback', { detail: { reason } }));
    suppressRazorpayAbortAlert();
    window.location.assign('/checkout');
  };

  const showFallbackDialog = (reason = 'razorpay-render-timeout') => {
    fallbackReason = reason;
    removeCheckoutLoader();
    if (fallbackDialog?.isConnected) return;

    fallbackDialog = document.createElement('dialog');
    fallbackDialog.className = 'hoy-checkout-fallback';
    fallbackDialog.setAttribute('aria-labelledby', 'HoyCheckoutFallbackTitle');
    fallbackDialog.setAttribute('aria-describedby', 'HoyCheckoutFallbackText');
    fallbackDialog.innerHTML = `
      <div class="hoy-checkout-fallback__content">
        <p class="hoy-checkout-fallback__eyebrow">Secure checkout</p>
        <h2 id="HoyCheckoutFallbackTitle" class="hoy-checkout-fallback__title">Checkout is taking longer than expected</h2>
        <p id="HoyCheckoutFallbackText" class="hoy-checkout-fallback__text">
          Your cart is still available. Continue through our secure checkout, or try again.
        </p>
        <div class="hoy-checkout-fallback__actions">
          <button type="button" class="hoy-checkout-fallback__primary" data-hoy-native-checkout>
            Continue to checkout
          </button>
          <button type="button" class="hoy-checkout-fallback__secondary" data-hoy-retry-razorpay>
            Try again
          </button>
        </div>
      </div>
    `;

    const styles = document.createElement('style');
    styles.textContent = `
      .hoy-checkout-fallback {
        width: min(420px, calc(100vw - 32px));
        max-width: 420px;
        margin: auto;
        padding: 0;
        border: 0;
        border-radius: 18px;
        color: #0a0f0e;
        background: #fff;
        box-shadow: 0 24px 80px rgb(0 0 0 / 30%);
      }
      .hoy-checkout-fallback::backdrop { background: rgb(10 15 14 / 65%); }
      .hoy-checkout-fallback__content { padding: 28px 24px 24px; text-align: left; }
      .hoy-checkout-fallback__eyebrow {
        margin: 0 0 8px;
        color: #347f66;
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.1em;
        text-transform: uppercase;
      }
      .hoy-checkout-fallback__title { margin: 0; font-size: 24px; line-height: 1.2; }
      .hoy-checkout-fallback__text { margin: 12px 0 22px; color: #3f4744; font-size: 15px; line-height: 1.5; }
      .hoy-checkout-fallback__actions { display: grid; gap: 10px; }
      .hoy-checkout-fallback__primary,
      .hoy-checkout-fallback__secondary {
        min-height: 48px;
        padding: 12px 18px;
        border: 1px solid #283e36;
        border-radius: 999px;
        font: inherit;
        font-weight: 600;
        cursor: pointer;
      }
      .hoy-checkout-fallback__primary { color: #fff; background: #283e36; }
      .hoy-checkout-fallback__secondary { color: #283e36; background: #fff; }
      .hoy-checkout-fallback__primary:focus-visible,
      .hoy-checkout-fallback__secondary:focus-visible { outline: 3px solid #6eb59d; outline-offset: 2px; }
      @media (max-width: 480px) {
        .hoy-checkout-fallback__content { padding: 24px 20px 20px; }
        .hoy-checkout-fallback__title { font-size: 21px; }
      }
    `;

    fallbackDialog.appendChild(styles);
    fallbackDialog.addEventListener('cancel', (event) => event.preventDefault());
    fallbackDialog
      .querySelector('[data-hoy-native-checkout]')
      ?.addEventListener('click', () => continueToNativeCheckout(fallbackReason));
    fallbackDialog.querySelector('[data-hoy-retry-razorpay]')?.addEventListener('click', () => window.location.reload());

    document.body.appendChild(fallbackDialog);
    fallbackDialog.showModal();
    fallbackDialog.querySelector('[data-hoy-native-checkout]')?.focus();
  };

  const startMagicAttempt = (focusReturnTarget) => {
    magicAttemptState = MAGIC_ATTEMPT_STATE.awaitingRender;
    checkoutFocusReturnTarget = focusReturnTarget;
    clearMagicRenderTimeout();
    scheduleCheckoutLoader();
    magicRenderTimeoutId = window.setTimeout(() => {
      magicRenderTimeoutId = undefined;
      if (magicAttemptState !== MAGIC_ATTEMPT_STATE.awaitingRender) return;

      magicAttemptState = MAGIC_ATTEMPT_STATE.recovery;
      showFallbackDialog('razorpay-render-timeout');
    }, MAGIC_RENDER_TIMEOUT_MS);
  };

  const finishMagicAttempt = (razorpayFrame) => {
    magicAttemptState = MAGIC_ATTEMPT_STATE.rendered;
    clearMagicRenderTimeout();
    removeCheckoutLoader();
    removeFallbackDialog();
    window.requestAnimationFrame(() => {
      if (magicAttemptState === MAGIC_ATTEMPT_STATE.rendered) focusWithoutScrolling(razorpayFrame);
    });
  };

  const resetMagicAttempt = () => {
    const focusReturnTarget = checkoutFocusReturnTarget;
    checkoutFocusReturnTarget = undefined;
    magicAttemptState = MAGIC_ATTEMPT_STATE.idle;
    nativeCheckoutInProgress = false;
    drawerCheckoutInProgress = false;
    checkoutReplayInProgress = false;
    fallbackReason = 'razorpay-render-timeout';
    clearMagicRenderTimeout();
    removeCheckoutLoader();
    removeFallbackDialog();
    restoreAlertGuard?.();
    window.requestAnimationFrame(() => {
      if (magicAttemptState === MAGIC_ATTEMPT_STATE.idle) focusWithoutScrolling(focusReturnTarget);
    });
  };

  const parseMessageData = (data) => {
    if (typeof data !== 'string') return data;

    try {
      return JSON.parse(data);
    } catch (_error) {
      return undefined;
    }
  };

  const getMatchingRazorpayFrame = (event) => {
    if (!event.source) return undefined;

    try {
      const origin = new URL(event.origin);
      const isRazorpayHost = RAZORPAY_ROOT_DOMAINS.some(
        (domain) => origin.hostname === domain || origin.hostname.endsWith(`.${domain}`)
      );
      if (origin.protocol !== 'https:' || !isRazorpayHost) return undefined;
    } catch (_error) {
      return undefined;
    }

    return Array.from(document.querySelectorAll(RAZORPAY_FRAME_SELECTOR)).find((frame) => {
      return frame instanceof HTMLIFrameElement && frame.contentWindow === event.source;
    });
  };

  window.addEventListener('message', (event) => {
    const razorpayFrame = getMatchingRazorpayFrame(event);
    if (!razorpayFrame) return;

    const payload = parseMessageData(event.data);
    if (!payload || typeof payload !== 'object') return;

    const isMagicV2Message = payload.source === 'frame';
    const isMagicV1Message = payload.source === 'sopc-frame';
    const messageType = isMagicV2Message ? payload.event : isMagicV1Message ? payload.topic : undefined;

    if (
      messageType === 'render' &&
      (magicAttemptState === MAGIC_ATTEMPT_STATE.awaitingRender ||
        magicAttemptState === MAGIC_ATTEMPT_STATE.recovery)
    ) {
      finishMagicAttempt(razorpayFrame);
      return;
    }

    if (
      (messageType === 'dismiss' || messageType === 'hidden' || messageType === 'close') &&
      (magicAttemptState === MAGIC_ATTEMPT_STATE.awaitingRender ||
        magicAttemptState === MAGIC_ATTEMPT_STATE.rendered)
    ) {
      resetMagicAttempt();
      return;
    }

    // A validated pre-render Razorpay fault means no payment has started. Give
    // the customer the same explicit recovery choice instead of leaving a loader.
    if (messageType === 'fault' && magicAttemptState === MAGIC_ATTEMPT_STATE.awaitingRender) {
      magicAttemptState = MAGIC_ATTEMPT_STATE.recovery;
      clearMagicRenderTimeout();
      showFallbackDialog('razorpay-pre-render-fault');
    }
  });

  const forceCloseCartDrawer = (dialog) => {
    const lockedScrollY = Math.abs(Number.parseInt(document.body.style.top || '0', 10)) || window.scrollY;

    if (dialog.open) dialog.close();
    dialog.classList.remove('dialog-closing');
    dialog.style.animation = '';
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: lockedScrollY, behavior: 'instant' });
  };

  const closeCartDrawer = async (drawer, dialog) => {
    if (typeof drawer.closeDialog !== 'function') {
      forceCloseCartDrawer(dialog);
      return;
    }

    await Promise.race([
      drawer.closeDialog(),
      new Promise((resolve) => window.setTimeout(resolve, DRAWER_CLOSE_TIMEOUT_MS)),
    ]);

    // Defensive fallback for browsers that do not complete the drawer animation.
    if (dialog.open) forceCloseCartDrawer(dialog);
  };

  window.addEventListener(
    'click',
    async (event) => {
      if (!(event.target instanceof Element)) return;

      // While checkout is opening, block extra storefront activations from
      // keyboard, assistive technology, or repeated taps. The one intentional
      // replay used after closing the cart drawer must still reach Razorpay.
      if (!checkoutReplayInProgress && magicAttemptState === MAGIC_ATTEMPT_STATE.awaitingRender) {
        event.preventDefault();
        event.stopImmediatePropagation();
        focusWithoutScrolling(checkoutLoader);
        return;
      }

      if (checkoutReplayInProgress) return;

      const checkoutControl = event.target.closest(CHECKOUT_CONTROL_SELECTOR);
      if (!(checkoutControl instanceof HTMLElement)) return;

      // Keep one checkout attempt active until Razorpay renders, closes, faults,
      // or the recovery timeout expires. This also protects the full cart page.
      if (magicAttemptState !== MAGIC_ATTEMPT_STATE.idle) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      const drawer = checkoutControl.closest(CART_DRAWER_SELECTOR);
      const dialog = drawer?.querySelector(CART_DRAWER_DIALOG_SELECTOR);
      const focusReturnTarget = drawer?.querySelector(CART_DRAWER_TRIGGER_SELECTOR) ?? checkoutControl;

      startMagicAttempt(focusReturnTarget);

      // The cart page has no modal drawer, so Razorpay can handle it normally.
      if (!(drawer instanceof HTMLElement) || !(dialog instanceof HTMLDialogElement)) return;

      // Stop the first click before Razorpay sees it. The drawer's native modal
      // occupies the browser top layer and can cover Razorpay's fixed iframe.
      event.preventDefault();
      event.stopImmediatePropagation();

      // Ignore a second tap while the first tap is closing and replaying the
      // checkout action. This prevents two Razorpay instances from opening.
      if (drawerCheckoutInProgress) return;

      drawerCheckoutInProgress = true;
      checkoutControl.setAttribute('aria-busy', 'true');

      try {
        try {
          await closeCartDrawer(drawer, dialog);
        } catch (_error) {
          forceCloseCartDrawer(dialog);
        } finally {
          checkoutControl.removeAttribute('aria-busy');
        }

        // If the defensive close timeout outlived the loader delay, focus may
        // have been rejected while the modal drawer still owned the top layer.
        focusWithoutScrolling(checkoutLoader);

        if (!checkoutControl.isConnected) {
          continueToNativeCheckout('checkout-control-removed');
          return;
        }

        // Replay after the top-layer cart dialog has closed. Razorpay keeps its
        // standard delegated handler and remains the primary checkout path. If
        // its script did not load, the form naturally falls back to Shopify.
        checkoutReplayInProgress = true;
        try {
          checkoutControl.click();
        } finally {
          checkoutReplayInProgress = false;
        }
      } finally {
        drawerCheckoutInProgress = false;
      }
    },
    true
  );

  window.addEventListener('pagehide', () => {
    clearMagicRenderTimeout();
    removeCheckoutLoader();
    if (nativeCheckoutInProgress) removeFallbackDialog();
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) resetMagicAttempt();
  });
})();
